// ContactList.jsx

import React, { useState, useEffect } from "react";
import { ColorRing } from "react-loader-spinner";
import useContactsList from "../../hooks/useContactsList";
import ContactsHeader from "./ContactsHeader";
import ContactsTable from "./ContactsTable";
import ContactsCardList from "../ContactCards/ContactCardList";
import AddContactModal from "../ContactModals/AddContactModal";
import ContactModal from "../ContactModals/ContactModal";
import { getContactById } from "../../services/contactService";
import Pagination from "@mui/material/Pagination";

import RowsPerPageSelector from "../../../../../../components/Tables/Pagination/RowsPerPageSelector";
import { useAuth } from "../../../../../../contexts/AuthContext";

const ContactList = () => {
  const { currentUser } = useAuth();
  const [selectedListType, setSelectedListType] = useState("allContacts");
  const [viewMode, setViewMode] = useState("table");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedContactData, setSelectedContactData] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const {
    contacts,
    contactFields,
    isLoading,
    pagination,
    setPagination,
    setDataVersion,
  } = useContactsList(selectedListType, searchQuery);

  // Reset pagination when selectedListType changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [selectedListType]);

  useEffect(() => {
    if (
      currentUser?.rowsPerPage &&
      currentUser.rowsPerPage !== pagination.size
    ) {
      setPagination((prev) => ({
        ...prev,
        size: currentUser.rowsPerPage,
        page: 0,
      }));
    }
  }, [currentUser?.rowsPerPage]);

  const handleDataChange = () => {
    setDataVersion((prev) => prev + 1);
  };

  const handleButtonClick = (listType) => {
    setSelectedListType(listType);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenCreateModal = () => setIsCreateOpen(true);
  const handleCloseCreateModal = () => setIsCreateOpen(false);

  const handleOpenViewModal = async (contactId) => {
    if (selectedListType === "employees") {
      // Do nothing for employees
      return;
    }

    if (!contactId) {
      console.error("No contact ID provided");
      alert("Invalid contact ID");
      return;
    }

    setIsModalLoading(true);
    setIsViewOpen(true);

    try {
      const contactData = await getContactById(contactId);
      setSelectedContactData(contactData);
      setIsModalLoading(false);
    } catch (error) {
      console.error("Error fetching contact:", error);
      setIsModalLoading(false);
      setIsViewOpen(false);
      alert("Error fetching contact data");
    }
  };

  const handleCloseViewModal = () => {
    setSelectedContactData(null);
    setIsViewOpen(false);
  };

  const setNewComponentRight = () => {
    const currentIndex = contacts.indexOf(selectedContactData);
    const nextIndex = (currentIndex + 1) % contacts.length;
    setSelectedContactData(contacts[nextIndex]);
  };

  const setNewComponentLeft = () => {
    const currentIndex = contacts.indexOf(selectedContactData);
    const prevIndex = (currentIndex - 1 + contacts.length) % contacts.length;
    setSelectedContactData(contacts[prevIndex]);
  };

  const displayedContacts = contacts;

  return (
    <div className="mx-auto pl-4 pr-4 h-screen w-full">
      <ContactsHeader
        selectedListType={selectedListType}
        handleButtonClick={handleButtonClick}
        searchQuery={searchQuery}
        handleSearchChange={(e) => setSearchQuery(e.target.value)}
        onSearch={handleSearch}
        viewMode={viewMode}
        setViewMode={setViewMode}
        handleOpenCreateModal={handleOpenCreateModal}
      />

      <div className="flex justify-center items-center">
        <ColorRing
          visible={isLoading}
          height="200"
          width="200"
          ariaLabel="color-ring-loading"
          wrapperStyle={{}}
          wrapperClass="color-ring-wrapper"
          colors={[
            "#033B5C",
            "#033B5C",
            "#033B5C",
            "#033B5C",
            "#033B5C",
            "#033B5C",
          ]}
        />
      </div>

      {!isLoading && viewMode === "table" && (
        <ContactsTable
          contactFields={contactFields}
          contacts={displayedContacts}
          handleOpenViewModal={handleOpenViewModal}
          selectedListType={selectedListType}
        />
      )}

      {!isLoading && viewMode === "card" && (
        <ContactsCardList
          contacts={displayedContacts}
          contactFields={contactFields}
          handleOpenViewModal={handleOpenViewModal}
          selectedListType={selectedListType}
        />
      )}

      {/* MUI Pagination Controls */}
      {[
        "allContacts",
        "managementCompanies",
        "customers",
        "buildings",
        "employees",
      ].includes(selectedListType) && (
        <div className="flex justify-between items-center mt-4 px-4 py-2 border-t border-gray-300 fixed bottom-0 left-0 w-full bg-white">
          {/* Pagination Controls */}
          <Pagination
            count={pagination.totalPages}
            page={pagination.page + 1} // Convert zero-indexed to one-indexed
            onChange={(e, value) => handlePageChange(value - 1)} // Convert one-indexed to zero-indexed
            color="primary"
            shape="rounded"
          />

          {/* Rows Per Page Selector */}
          <div className="flex items-center space-x-2">
            <RowsPerPageSelector />
          </div>
        </div>
      )}

      <ContactModal
        open={isViewOpen}
        handleClose={handleCloseViewModal}
        contactData={selectedContactData}
        isLoading={isModalLoading}
        setNext={setNewComponentRight}
        setPrev={setNewComponentLeft}
        handleDataChange={handleDataChange}
      />

      <AddContactModal
        open={isCreateOpen}
        handleClose={handleCloseCreateModal}
        handleDataChange={handleDataChange}
      />
    </div>
  );
};

export default ContactList;
